// === useGameState.js ===
// Custom React hook for Chess Tile Slider game state and handlers
// All board/tile state, movement, and game logic is managed here.
//
// ---
//
// This module strictly follows the static map-based template architecture:
// - Board and tile state are initialized from static, read-only templates (STARTING_SQUARE_MAP, STARTING_TILE_MAP).
// - All board/tile mapping uses fileRank as the only identifier (never squareId or similar).
// - At game start, deep copies of templates are made for inPlaySquares and inPlayTiles.
// - One color tile (never a chess piece) is removed to create the empty square (the removed tile is deleted from inPlayTiles).
// - All movement, shuffle, and solved state logic is implemented per requirements/technical-specification.
// - The empty square is ignored for win detection; only the 63 placed tiles are checked for solved state.
// - State setters (setInPlaySquares, setInPlayTiles, setEmptySquare, setMoveHistory) are exposed for board restoration/reset functionality.
//
// See requirements.md and technical-specification.md for full details.

import React, { useState, useRef } from 'react';
import {
  getInitialInPlaySquares,
  getInitialInPlayTiles,
  removeTileFromBoard,
  getMovableTileIdsByRules
} from '../utils/gameUtils';

// === Section: Helper Functions (Internal) ===
/**
 * Checks if a tile is movable (according to chess-move-based rules).
 * @param {string} tileId
 * @param {Object} inPlaySquares - Map of fileRank to square object
 * @param {string} emptySquare - fileRank of the empty square
 * @param {Object} inPlayTiles - Map of tileId to tile object
 * @param {boolean} testMoveMode - Bypass chess rules if true (for testing)
 * @returns {boolean}
 */
function isTileMovable(tileId, inPlaySquares, emptySquare, inPlayTiles, testMoveMode = false) {
  if (testMoveMode) return true;
  const movableIds = getMovableTileIdsByRules(inPlaySquares, inPlayTiles, emptySquare);
  return movableIds.includes(tileId);
}

// === Section: useGameState Hook ===
/**
 * useGameState - Encapsulates all game state and handlers for Chess Tile Slider.
 *
 * State managed here:
 *   - inPlaySquares: { [fileRank]: SquareObj } (current board state)
 *   - inPlayTiles: { [tileId]: TileObj } (current tile state)
 *   - emptySquare: fileRank of the empty square
 *   - moveHistory: array of move objects (for undo)
 *   - settings: game settings (object)
 *
 * All board/tile state is initialized from static templates (see requirements.md).
 * All mapping uses fileRank as the only identifier.
 * The empty square is always a displaced color tile (never a chess piece).
 *
 * Returns state and handler functions for use in App.js and child components.
 */
export default function useGameState() {
  // === Section: Solve Moves State ===
  // Indicates if Solve Moves mode is active
  const [solveMovesActive, setSolveMovesActive] = useState(false);

  // Returns the current solve move (top of stack) or null
  const getCurrentSolveMove = React.useCallback(() => {
    if (!solveMovesActive || !shuffleStepsRef.current.length) return null;
    return shuffleStepsRef.current[shuffleStepsRef.current.length - 1];
  }, [solveMovesActive]);

  // Pops and applies the current solve move (if any)
  function applyCurrentSolveMove() {
    if (!solveMovesActive || !shuffleStepsRef.current.length) return false;
    const move = shuffleStepsRef.current.pop();
    // Only allow the move if the board matches the expected state
    // (tile must be at move.from, emptySquare must be at move.to)
    const tile = inPlayTiles[move.tileId];
    if (!tile || tile.fileRank !== move.from || emptySquare !== move.to) {
      // Invalid state, do not apply
      return false;
    }
    // Move the tile as in handleMoveTile, but bypass normal validation
    const newSquares = { ...inPlaySquares };
    newSquares[move.from] = { ...newSquares[move.from], tileId: null };
    newSquares[move.to] = { ...newSquares[move.to], tileId: move.tileId };
    const newTiles = { ...inPlayTiles };
    newTiles[move.tileId] = { ...newTiles[move.tileId], fileRank: move.to };
    Object.values(newTiles).forEach(t => {
      t.isSolved = Array.isArray(t.solved) && t.solved.includes(t.fileRank);
    });
    setInPlaySquares(newSquares);
    setInPlayTiles(newTiles);
    setEmptySquare(move.from);
    setMoveHistory([...moveHistory, { from: move.from, to: move.to, tileId: move.tileId }]);
    // If stack is now empty, auto-disable solve mode
    if (shuffleStepsRef.current.length === 0) setSolveMovesActive(false);
    return true;
  }

  // Returns the tileId of the currently highlighted solve move, or null
  const getSolveMoveTileId = React.useCallback(() => {
    // eslint-disable-next-line no-console
    console.log('[DEBUG] getSolveMoveTileId called. solveMovesActive:', solveMovesActive, 'shuffleStepsRef.current:', shuffleStepsRef.current);
    const move = getCurrentSolveMove();
    // eslint-disable-next-line no-console
    console.log('[DEBUG] getSolveMoveTileId move:', move);
    return move ? move.tileId : null;
  }, [getCurrentSolveMove, solveMovesActive]);
  // === Section: State ===
  // === Section: Shuffle Steps Ref (for Solve Moves) ===
  // Stores the stack of shuffle steps to enable guided solution replay
  const shuffleStepsRef = useRef([]);
  // Shuffle mode (easy, hard, expert)
  const [shuffleMode, setShuffleMode] = useState('easy');
  const [inPlaySquares, setInPlaySquares] = useState(getInitialInPlaySquares());
  const [inPlayTiles, setInPlayTiles] = useState(getInitialInPlayTiles());
  // The empty square is not set at initialization; it is set only after a tile is removed
  const [emptySquare, setEmptySquare] = useState(null);
  const [moveHistory, setMoveHistory] = useState([]);
  const [settings, setSettings] = useState({
    chessMovesEnabled: true,
    lineOfSightRequired: true,
    showMoveHints: false,
    showFileRank: false,
    showSolvedIndicator: false,
    debugMode: false,
    testMoveMode: false
  });
  const [isShuffled, setIsShuffled] = useState(false); // Tracks if the board has been shuffled

  // === Section: Undo/Redo History ===
  const historyRef = useRef([]);

  // === Section: Move Tile Handler ===
  /**
   * Moves a tile to the empty square if valid.
   * Only tiles adjacent to the empty square (per movement rules) may move.
   * All moves must target the empty square (fileRank).
   * @param {string} tileId - The tile to move.
   * @returns {boolean} - True if move was successful.
   */
  function handleMoveTile(tileId, dryRun = false) {
    // If Solve Moves is active, only allow the highlighted tile to move
    if (solveMovesActive) {
      const solveMove = getCurrentSolveMove();
      if (!solveMove || tileId !== solveMove.tileId) {
        // Invalid move: not the highlighted tile
        // Optionally: trigger invalid move feedback here
        return false;
      }
      // Allow the highlighted tile to move to the empty square, regardless of board state
      if (dryRun) return true;
      // Pop and apply the solve move (remove from stack)
      shuffleStepsRef.current.pop();
      // Mutate state as in normal move
      const fromSquare = inPlayTiles[tileId].fileRank;
      const toSquare = emptySquare;
      const newSquares = { ...inPlaySquares };
      newSquares[fromSquare] = { ...newSquares[fromSquare], tileId: null };
      newSquares[toSquare] = { ...newSquares[toSquare], tileId: tileId };
      const newTiles = { ...inPlayTiles };
      newTiles[tileId] = { ...newTiles[tileId], fileRank: toSquare };
      Object.values(newTiles).forEach(t => {
        t.isSolved = Array.isArray(t.solved) && t.solved.includes(t.fileRank);
      });
      setInPlaySquares(newSquares);
      setInPlayTiles(newTiles);
      setEmptySquare(fromSquare);
      setMoveHistory([...moveHistory, { from: fromSquare, to: toSquare, tileId }]);
      // If stack is now empty, auto-disable solve mode
      if (shuffleStepsRef.current.length === 0) setSolveMovesActive(false);
      return true;
    }
    // Normal move logic (not in Solve Moves mode)
    const tile = inPlayTiles[tileId];
    if (!tile) {
      return false;
    }
    if (!isTileMovable(tileId, inPlaySquares, emptySquare, inPlayTiles, settings && settings.testMoveMode)) {
      return false;
    }
    if (dryRun) return true;
    // Save history for undo
    historyRef.current.push({
      inPlaySquares: { ...inPlaySquares },
      inPlayTiles: { ...inPlayTiles },
      emptySquare,
      moveHistory: [...moveHistory]
    });
    // Mutate state
    const fromSquare = tile.fileRank;
    const toSquare = emptySquare;
    // Update inPlaySquares
    const newSquares = { ...inPlaySquares };
    newSquares[fromSquare] = { ...newSquares[fromSquare], tileId: null };
    newSquares[toSquare] = { ...newSquares[toSquare], tileId };
    // Update inPlayTiles
    const newTiles = { ...inPlayTiles };
    newTiles[tileId] = { ...newTiles[tileId], fileRank: toSquare };
    // === Update isSolved for all tiles ===
    Object.values(newTiles).forEach(t => {
      t.isSolved = Array.isArray(t.solved) && t.solved.includes(t.fileRank);
    });
    setInPlaySquares(newSquares);
    setInPlayTiles(newTiles);
    setEmptySquare(fromSquare);
    setMoveHistory([...moveHistory, { from: fromSquare, to: toSquare, tileId }]);
    return true;
  }

  // === Section: Shuffle Handler ===
  /**
   * Shuffles the board by making random valid moves.
   * Ensures the empty square is not isolated after shuffle.
   * @param {number} moves - Number of shuffle moves.
   */
  function handleShuffle(moves = 50) {
    let squares = { ...getInitialInPlaySquares() };
    let tiles = { ...getInitialInPlayTiles() };
    let empty = emptySquare;
    let history = [];
    let lastMovedTileId = null;
    let i = 0;
    while (i < moves) {
      let movableIds = getMovableTileIdsByRules(squares, tiles, empty);
      // Exclude the tile that was just moved in the previous step to prevent immediate undo
      const prevMovedTileId = lastMovedTileId;
      if (prevMovedTileId) {
        movableIds = movableIds.filter(id => id !== prevMovedTileId);
      }
      if (movableIds.length === 0) {
        break;
      }
      const randomId = movableIds[Math.floor(Math.random() * movableIds.length)];
      const from = tiles[randomId].fileRank;
      // Mutate
      tiles[randomId].fileRank = empty;
      squares[empty].tileId = randomId;
      squares[from].tileId = null;
      history.push({ from, to: empty, tileId: randomId });
      lastMovedTileId = randomId;
      empty = from;
      i++;
    }
    // === Update isSolved for all tiles after shuffle ===
    Object.values(tiles).forEach(t => {
      t.isSolved = Array.isArray(t.solved) && t.solved.includes(t.fileRank);
    });
    // === Logging: Setting up Solve Moves stack ===
    // eslint-disable-next-line no-console
    console.log('[SolveMoves] Shuffle steps stack initialized:', JSON.stringify(history));
    setInPlaySquares({ ...squares });
    setInPlayTiles({ ...tiles });
    setEmptySquare(empty);
    setMoveHistory([]);
    historyRef.current = [];
    setIsShuffled(true); // Mark as shuffled after shuffle
    shuffleStepsRef.current = [...history]; // Save shuffle steps for Solve Moves
    return history; // Return shuffle steps
  }

  // === Section: Undo Handler ===
  /**
   * Undoes the last move (restores previous state from history).
   */
  function handleUndo() {
    if (historyRef.current.length === 0) {
      return;
    }
    const last = historyRef.current.pop();
    setInPlaySquares(last.inPlaySquares);
    setInPlayTiles(last.inPlayTiles);
    setEmptySquare(last.emptySquare);
    setMoveHistory(last.moveHistory);
  }

  // === Section: Remove Tile Handler ===
  /**
   * Removes a tile from the board and updates state.
   * Used only to create the empty square at game start (removes a color tile).
   * The removed tile is deleted from inPlayTiles and is ignored for solved state.
   * @param {string} tileId
   */
  function handleRemoveTile(tileId) {
    const gameState = { inPlaySquares, inPlayTiles, emptySquare };
    removeTileFromBoard(gameState, tileId);
    // Debug: Log board state after tile removal
    // (logging removed)
    // Log solved state for all tiles after removal
    Object.values(gameState.inPlayTiles).forEach(t => {
    });
    const allSolved = Object.values(gameState.inPlayTiles).every(t => t.fileRank !== null && t.isSolved);
    setInPlaySquares({ ...gameState.inPlaySquares });
    setInPlayTiles({ ...gameState.inPlayTiles });
    setEmptySquare(gameState.emptySquare);
    setIsShuffled(false); // Reset shuffle state when a new game starts (tile removed)
  }

  // === Section: Power-Up Handler (Stub) ===
  /**
   * Handles power-up activation (stub; implement as needed).
   * @param {string} type
   */
  function handlePowerUp(type) {
    // ...implement power-up logic...
  }

  // === Section: Settings Change Handler ===
  /**
   * Updates settings state.
   * @param {Object} newSettings
   */
  function handleSettingsChange(newSettings) {
    setSettings(prev => ({ ...prev, ...newSettings }));
  }

  // === Section: Return API (Minimal) ===
  return {
    inPlaySquares,
    inPlayTiles,
    emptySquare,
    moveHistory,
    settings,
    isShuffled,
    shuffleMode,
    setShuffleMode,
    handleMoveTile,
    handleRemoveTile,
    handleUndo,
    handleShuffle,
    handlePowerUp,
    handleSettingsChange,
    // === Solve Moves API ===
    solveMovesActive,
    setSolveMovesActive,
    getCurrentSolveMove,
    applyCurrentSolveMove,
    getSolveMoveTileId,
    // === State setters for board restore/reset functionality ===
    setInPlaySquares, // For restoring board state (e.g., Reset Tiles)
    setInPlayTiles,
    setEmptySquare,
    setMoveHistory,
    setIsShuffled,
    // === Expose shuffle steps for Solve Moves ===
    shuffleStepsRef
  };
}
// === End useGameState.js ===
